/** Reports: the filings owed, and one filing opened to read, check and issue.
 *
 * **This is a preview, not a filing, and the difference is the whole of what is still missing.**
 * A filed report is an *artifact*: hashed, pinned to a ledger state, reproducible byte for byte
 * from the same data (ADR-0020, built). What a screen shows before that is a report rebuilt from
 * today's figures, so the screen says what it is at the top, where it cannot be missed.
 *
 * **The place is Filings (ADR-0212 decision 6).** One tab per regime the product has loaded; a
 * filing opens as its page - the readiness percentage, the path and the to-do list
 * (`filings/FilingPage.tsx`). With `?filing=<obligation id>&tab=<view>` it is that filing's long view
 * (`ReportsDetail`: Disclosures, Checks, Checklist, Findings, Year-on-year, Issue, Submission pack),
 * where the text the page itself does not carry lives.
 * A report is about an **obligation**, never about a regime and a date range (ADR-0029, F02): a
 * dual-market group has six, two entities file separately, and keying by regime showed whichever
 * sorted first.
 *
 * **A filing that is not on offer is not in force, and it is said.** `GET /reports/obligations/{id}`
 * resolves and authorizes before it describes anything, so a pasted id is not a security question;
 * it is a *silent substitution* question. A link naming a filing this workspace does not have used
 * to open a complete report for the wrong company with no sign the link was not honoured. It now
 * opens the list with a sentence saying so, and substitutes nothing.
 *
 * Three things the disclosures must not do, each of which would be easy and wrong:
 *
 * - **Show a zero for a line it cannot produce.** An intensity ratio needs a denominator the
 *   company chooses; a methodologies statement is words the company writes.
 * - **Fold the guidance line into the total.** SECR's 20D(1) names two activities rather than a
 *   scope, so everything else in Scope 1 sits on its own line marked as guidance (B-19).
 * - **Look like it has filed something.**
 */

import { RequireSession } from "../shell/RequireSession";
import { useArtifacts, useObligations } from "../api/queries";
import { ReportsDetail } from "./ReportsDetail";
import { FilingsPlace } from "./filings/FilingsPlace";
import { isDetailTab } from "./reportsModel";
import { ScreenFrame } from "./ScreenFrame";
import { useScreen } from "./useScreen";

function ReportsScreenBody() {
  const screen = useScreen();
  /** The read, not `data = []`: a failed obligations read is the place's own failed state, and an
   * empty list is a statement about the company (EXP-29). */
  const obligationsRead = useObligations();
  const asked = screen.filing;
  const active = asked ? obligationsRead.data?.find((o) => o.obligation_id === asked) : undefined;
  // The filings issued for the open obligation, newest first. A receipt opened over this screen is
  // told the latest one, so it states the rules version that filing used (UX-02).
  const issued = useArtifacts(active?.obligation_id);
  const latestIssued = issued.data?.[0]?.id;

  return (
    <ScreenFrame
      screen={screen}
      title="Filings"
      // The body scrolls: a filing's long views can be taller than the viewport. The page still
      // does not scroll, which is the shell's actual rule.
      bodyScrolls
      receiptFiling={latestIssued}
    >
      {active && isDetailTab(screen.tab) ? (
        <ReportsDetail screen={screen} active={active} />
      ) : (
        <FilingsPlace method={screen.method} filingId={asked} tab={screen.tab} />
      )}
    </ScreenFrame>
  );
}

/** Wrapped, because there is no unauthenticated surface any more: every endpoint these panels read
 * takes no organisation and answers only to a session. Without the wrapper the screen would render
 * its empty state while every query 401s, which looks like a workspace with no data rather than a
 * person who is not signed in. */
export function ReportsScreen() {
  return (
    <RequireSession>
      <ReportsScreenBody />
    </RequireSession>
  );
}
